// Browser notifications — fires for high-priority SSE events even when the
// tab is minimized or in the background. Requires user permission (asked once
// after login). Falls back silently if permission is denied or unsupported.

let permissionRequestedThisSession = false;

export type NotifKind =
  | 'OTP_READY'
  | 'PENDING_REVIEW'
  | 'BENEFICIARY_HOLD'
  | 'AMBIGUOUS_HOLD'
  | 'PARKED'
  | 'TRANSACTION_UNATTRIBUTED'
  | 'ATTRIBUTION_REMINDER'
  | 'ATTRIBUTION_ESCALATED'
  | 'WALLET_VARIANCE_FLAGGED';

export function isNotifSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export async function ensureNotifPermission(): Promise<NotificationPermission> {
  if (!isNotifSupported()) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  if (permissionRequestedThisSession) return Notification.permission;
  permissionRequestedThisSession = true;
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

/** Distinct alert tone for each notification class. Browsers don't let
 *  us specify a custom sound on `new Notification()`, so we play a
 *  synthesized tone via Web Audio alongside the OS notification.
 *
 *  - `otp`      — single high "ding"  → "OTP just arrived, type it in"
 *  - `approval` — two-tone descending → "someone needs your tap"
 *  - `mute`     — no tone (parking-lot reminders, escalation, etc) */
type AlertTone = 'otp' | 'approval' | 'mute';

let audioCtx: AudioContext | null = null;

// True when the user is actively looking at this exact tab. Skip audio
// AND visual cues when this is true — the event is already visible in
// the UI, so any beep is noise.
function isTabActive(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible' && document.hasFocus();
}

function playTone(tone: AlertTone): void {
  if (tone === 'mute') return;
  if (typeof window === 'undefined') return;
  // Skip the tone when the user is on this tab. Continuous chimes with
  // no visible notification (bug reported 2026-07-07) came from tone
  // firing on every SSE event regardless of tab focus.
  if (isTabActive()) return;
  try {
    // Lazy-init AudioContext on first use — Chrome requires a user
    // gesture before audio works, but a granted Notification.permission
    // implies the user has already interacted with the page.
    if (!audioCtx) audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const now = ctx.currentTime;
    if (tone === 'otp') {
      // Bright "ding": single sine at 1175 Hz (D6) with quick decay.
      // Distinctive, attention-grabbing, ~250ms total.
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1175;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.22, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    } else {
      // Two-tone "dum-dum": triangle 660 Hz → 440 Hz, 120 ms each.
      // Slower, lower, signals "respond / approve" rather than urgency.
      const make = (freq: number, t0: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + t0);
        gain.gain.linearRampToValueAtTime(0.2, now + t0 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t0 + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t0);
        osc.stop(now + t0 + dur + 0.02);
      };
      make(660, 0, 0.16);
      make(440, 0.18, 0.22);
    }
  } catch {
    // AudioContext can throw on autoplay-restricted browsers — silent
    // fallback (the OS notification itself will still fire).
  }
}

interface FireOpts {
  title: string;
  body: string;
  /** Path inside the SPA to navigate to when the user clicks the notification. */
  navigateTo?: string;
  /** Tag — multiple notifications with same tag collapse into one (e.g. parking-lot count). */
  tag?: string;
  /** Renotify even if a previous notification with the same tag is still showing. */
  renotify?: boolean;
  /** Audible / vibration. Default true for OTP-arrival and approval-needed kinds. */
  silent?: boolean;
  /** Which synthesized tone to play alongside the OS notification. */
  tone?: AlertTone;
  /** Keep the notification on screen until the user dismisses it
   *  (instead of Chrome's ~20s auto-fade). Used for OTPs. */
  requireInteraction?: boolean;
  /** When set, clicking the notification copies JUST this code to the
   *  clipboard — the OTP only, never the SMS body — so the user can
   *  paste straight into the bank page. */
  otpCode?: string;
}

export function fire(opts: FireOpts) {
  if (!isNotifSupported() || Notification.permission !== 'granted') return;
  // Skip everything when this tab is the active window — the event is
  // already surfacing in the UI (live SSE update). Tone AND OS banner
  // both get suppressed together to avoid the "continuous beep, no
  // notification visible" bug.
  if (isTabActive()) return;
  if (opts.tone) playTone(opts.tone);
  // Pre-copy the OTP code so paste works without clicking the banner.
  // Requires the tab is focused (or was recently); background tabs may
  // silently fail — the click handler is the fallback.
  if (opts.otpCode) {
    void navigator.clipboard.writeText(opts.otpCode).catch(() => {});
  }
  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
      // @ts-expect-error renotify is widely supported but missing from older lib.dom types
      renotify: opts.renotify,
      silent: opts.silent,
      icon: '/logo.png',
      // Never require interaction for OTP-class notifications — ops
      // reads the code (already in clipboard) and moves on. We also
      // schedule an explicit .close() below in case the OS's default
      // auto-dismiss window is longer than 8s.
      requireInteraction: opts.requireInteraction ?? false,
    });
    n.onclick = () => {
      window.focus();
      if (opts.otpCode) {
        // Belt-and-braces re-copy on click. First copy above may have
        // failed silently on background tabs.
        void navigator.clipboard.writeText(opts.otpCode).catch(() => {});
      }
      if (opts.navigateTo) {
        // Soft nav — works because we're a SPA. Fallback to hard reload if not.
        try {
          window.history.pushState({}, '', opts.navigateTo);
          window.dispatchEvent(new PopStateEvent('popstate'));
        } catch {
          window.location.href = opts.navigateTo;
        }
      }
      n.close();
    };
    // Auto-dismiss after 8s for OTP banners. Non-OTP notifications
    // (e.g. approvals) keep the OS's default auto-hide behavior.
    if (opts.otpCode) {
      setTimeout(() => {
        try { n.close(); } catch { /* already closed */ }
      }, 8000);
    }
  } catch {
    // Some browsers throw if permission revoked between check and call — ignore.
  }
}

// ── Badge: tab-title + app-icon count for an unhandled OTP ──────────
// The OS notification toast is suppressed while the tab is focused, so
// a focused-but-on-another-tab user had no signal. The title badge
// shows on the browser tab regardless of focus; setAppBadge dots the
// installed-PWA / taskbar icon. Both clear when the OTP is dismissed.
let baseTitle: string | null = null;
type BadgeNav = Navigator & {
  setAppBadge?: (n?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function setOtpBadge(): void {
  if (typeof document === 'undefined') return;
  if (baseTitle == null) baseTitle = document.title;
  document.title = `🔔 OTP ready · ${baseTitle}`;
  try {
    void (navigator as BadgeNav).setAppBadge?.(1);
  } catch {
    // Badging API unsupported / PWA not installed — title badge still shows.
  }
}

export function clearOtpBadge(): void {
  if (typeof document === 'undefined') return;
  if (baseTitle != null) document.title = baseTitle;
  try {
    void (navigator as BadgeNav).clearAppBadge?.();
  } catch {
    // ignore
  }
}

// SSE-replay dedup: EventSource auto-reconnects (laptop sleep, tab idle,
// network blip) replay buffered events via Last-Event-ID, which re-fires
// the OS notification. Track recently-notified otpLogIds in localStorage
// so the same OTP doesn't pop a fresh toast on every reconnect.
const OTP_NOTIFY_TTL_MS = 30 * 60_000;
const OTP_NOTIFY_LS_KEY = 'otp_shield_seen_otp_log_ids';
function hasNotifiedRecently(otpLogId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(OTP_NOTIFY_LS_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const now = Date.now();
    const ts = map[otpLogId];
    if (ts && now - ts < OTP_NOTIFY_TTL_MS) return true;
    map[otpLogId] = now;
    for (const [k, v] of Object.entries(map)) {
      if (now - v > OTP_NOTIFY_TTL_MS) delete map[k];
    }
    localStorage.setItem(OTP_NOTIFY_LS_KEY, JSON.stringify(map));
    return false;
  } catch {
    return false;
  }
}

interface SsePayload extends Record<string, unknown> {
  vendor?: string;
  amount?: string;
  currency?: string;
  beneficiaryName?: string;
  ageMinutes?: number;
  reminderNumber?: number;
  walletId?: string;
  walletName?: string;
  otpCode?: string;
  otpLogId?: string;
  cardNickname?: string;
  date?: string;
}

/** Map an SSE event into a Notification. Returns true if a notification fired. */
export function notifyForSseEvent(eventType: string, payload: SsePayload): boolean {
  const amount = payload.amount ? `₹${payload.amount}` : '';
  const vendor = payload.vendor ?? payload.beneficiaryName ?? '';

  switch (eventType) {
    case 'otp_ready': {
      if (typeof payload.otpLogId === 'string' && hasNotifiedRecently(payload.otpLogId)) return false;
      // The OS banner for OTPs is owned by the service worker's push
      // handler (sw.js). Calling fire() here would render a SECOND
      // banner from the page side whenever the tab is backgrounded —
      // that's the "OTP buzzed multiple times" bug from 2026-05-24.
      // Play the audio tone (alert when the user is on another tab)
      // and let SW render the banner exclusively.
      playTone('otp');
      return true;
    }
    case 'otp_watch_alert': {
      if (typeof payload.otpLogId === 'string' && hasNotifiedRecently(payload.otpLogId)) return false;
      // Same reasoning as otp_ready — SW owns the OS banner via web
      // push; the page only plays the alert tone.
      playTone('otp');
      return true;
    }
    case 'request_pending':
    case 'pending_review':
    case 'ambiguous_hold': {
      // Per-request tag so cross-device dismissal can target THIS specific
      // approval banner via `approval_<requestId>`. A static `'approval'`
      // tag would either collapse unrelated approvals together or fail
      // to dismiss only the resolved one.
      const reqId = typeof (payload as Record<string, unknown>)['requestId'] === 'string'
        ? (payload as Record<string, unknown>)['requestId'] as string
        : (typeof (payload as Record<string, unknown>)['request'] === 'object'
            && (payload as { request?: { id?: string } }).request?.id) || null;
      const tag = reqId ? `approval_${reqId}` : 'approval';
      fire({
        title: 'Approval needed' + (vendor ? ` · ${vendor}` : ''),
        body: amount ? `${amount} — tap to review` : 'Tap to review',
        navigateTo: '/approvals',
        tag,
        tone: 'approval',
      });
      return true;
    }
    case 'beneficiary_hold':
      fire({
        title: '🚨 Beneficiary hold',
        body: payload.beneficiaryName ?? 'New payee — review the addition',
        navigateTo: '/approvals',
        tag: 'beneficiary',
        tone: 'approval',
      });
      return true;
    case 'parked':
    case 'transaction_unattributed':
      fire({
        title: 'Parking lot · ' + (vendor || 'untagged debit'),
        body: amount ? `${amount} — attribute this debit` : 'Attribute this debit',
        navigateTo: '/parking-lot',
        tag: 'parking',
      });
      return true;
    case 'attribution_reminder':
      fire({
        title: `Tag this txn (reminder #${payload.reminderNumber ?? '?'})`,
        body: [vendor, amount, `${payload.ageMinutes ?? '?'} min ago`].filter(Boolean).join(' · '),
        navigateTo: '/parking-lot',
        tag: 'attrib-reminder',
        renotify: true,
      });
      return true;
    case 'attribution_escalated':
      fire({
        title: '🚨 Untagged 4hr+ — escalated',
        body: [vendor, amount].filter(Boolean).join(' · ') || 'Investigate',
        navigateTo: '/parking-lot',
        tag: 'attrib-escalated',
        renotify: true,
      });
      return true;
    case 'debit_disambiguation':
      fire({
        title: 'Confirm your payment',
        body: amount
          ? `A ${amount} debit landed and could be yours — claim it from Recent debits if you made it`
          : 'A debit may be yours — check Recent debits',
        navigateTo: '/recent-debits',
        tag: 'debit-disambiguation',
        renotify: true,
      });
      return true;
    case 'wallet_freed':
      fire({
        title: `${payload.walletName ?? 'Wallet'} is free`,
        body: 'The freeze is over — you can raise an OTP for it now.',
        navigateTo: '/',
        tag: 'wallet_freed',
        tone: 'otp',
      });
      return true;
    case 'wallet_variance_flagged':
      fire({
        title: 'Wallet variance flagged',
        body: `${payload.date ?? ''} — investigate the missing entry`,
        navigateTo: '/reconcile',
        tag: 'wallet-var',
      });
      return true;
    default:
      return false;
  }
}
